import { getSafeRedirect } from "@/lib/utils";
import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blueskydataplatform/shared-ui/components/ui/card";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/logo";
import useQueryParams from "@/hooks/use-query-params";
import { useContactUs } from "@blueskydataplatform/shared-ui/modules/enterprise/enterpise-contact-provider";
import { useAccountInfo } from "@/features/account/api/get-account-info";
import { useMemo } from "react";
import FinishMCPRegistration from "./finish-mcp-registration";
import { useMcpProvider } from "@/providers/mcp-providers-context";

const FinishRegistration = () => {
  const { isFromMCP, redirect: redirectParam } = useQueryParams();
  const redirect = getSafeRedirect(redirectParam);
  const openEnterpriseContact = useContactUs();
  const { data: accountInfo, isLoading: isAccountInfoLoading } = useAccountInfo();
  const { provider: mcpProvider } = useMcpProvider();
  const accountData = useMemo(() => {
    if (!accountInfo) return { origin: "mcp" };
    let name = accountInfo.account.accountName;

    if (accountInfo.account.firstName && accountInfo.account.lastName) {
      name = accountInfo.account.firstName + " " + accountInfo.account.lastName;
    }

    return {
      email: accountInfo.account.email,
      name,
      tel: accountInfo.account.phone || "",
      company: accountInfo.org.orgName,
      datasets: ["MT Newswires"],
      origin: "mcp",
      isUser: true,
      provider: mcpProvider,
      orgType: accountInfo.org.orgType,
    };
  }, [accountInfo, mcpProvider]);

  if (isFromMCP) {
    return <FinishMCPRegistration accountData={accountData} isLoading={isAccountInfoLoading} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-2xl shadow-sm">
        <CardHeader className="space-y-3">
          <div className="flex justify-center">
            <Logo className="mb-4" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Check your email</CardTitle>
          <CardDescription className="text-center max-w-md mx-auto">
            A confirmation email has been sent to your address. Please verify your email to complete your registration.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isFromMCP && (
            <div className="mb-6 rounded border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
              Your email address must be verified before you can access the MT Newswire MCP service. Please confirm your
              email and then return to continue.
            </div>
          )}
          {isFromMCP && (
            <div>
              <div className="mb-6 rounded border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
                The access to this MCP server requires an active MT Newswire MCP subscription.
              </div>
              <div className="flex items-center justify-center">
                <Button onClick={() => openEnterpriseContact(accountData)} disabled={isLoading}>
                  Request Subscription
                </Button>
              </div>
            </div>
          )}

          {isFromMCP && (
            <div className="relative flex items-center justify-center my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500 font-medium">OR</span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-center">
            <a href={redirect || "/console"} target="_self" className="inline-flex">
              <Button className="gap-2">
                {redirect ? "Complete your subscription" : "Go to Console"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FinishRegistration;
